import { Chip } from '@mui/material'
import { CheckCircleOutline, HelpOutline, RemoveCircleOutline } from '@mui/icons-material'
import { RegulationStatus } from '../../../shared/services/regulation-applicability/regulation-applicability.model'
import { CertificationStatus } from '../../../shared/services/management-system-certification/management-system-certification.model'

// Shared presentation vocabulary for regulation applicability, used by the Reporting page
// card and the dedicated regulations page so a status never renders two different ways.

export const STATUS_LABEL: Record<RegulationStatus, string> = {
  'applies': 'Applies',
  'may-apply': 'May apply',
  'not-applicable': 'Not applicable',
  'insufficient-data': 'More data needed',
}

// Subtle, no red: applies = brand green fill, may-apply = outlined warning tone,
// the two unknown/negative states = quiet grey.
export const statusChip = (status: RegulationStatus) => {
  switch (status) {
    case 'applies':
      return <Chip size='small' color='primary' label={STATUS_LABEL[status]} />
    case 'may-apply':
      return <Chip size='small' color='warning' variant='outlined' label={STATUS_LABEL[status]} />
    default:
      return <Chip size='small' variant='outlined' sx={{ color: 'text.secondary' }} label={STATUS_LABEL[status]} />
  }
}

export const criterionIcon = (met: boolean | null) => {
  if (met === true) return <CheckCircleOutline fontSize='small' color='primary' />
  if (met === false) return <RemoveCircleOutline fontSize='small' sx={{ color: 'text.secondary' }} />
  return <HelpOutline fontSize='small' sx={{ color: 'text.secondary' }} />
}

export const formatValue = (criterion: string, value: number | string | null): string => {
  if (value === null || value === undefined) return 'not set'
  if (typeof value === 'number') {
    const prefix = criterion === 'turnover' || criterion === 'balanceSheet' ? '£' : ''
    return `${prefix}${value.toLocaleString('en-GB')}`
  }
  return String(value)
}

// Management-system certificate status, shared by the Certifications tab, the supplier page and
// the ISO 14001 evidence page. Same palette as the regulation chips: no red.
export const CERTIFICATION_STATUS_LABEL: Record<CertificationStatus, string> = {
  'current': 'Current',
  'expiring_soon': 'Expires soon',
  'expired': 'Expired',
  'no_expiry_recorded': 'No expiry recorded',
}

export const certificationStatusChip = (status: CertificationStatus) => {
  switch (status) {
    case 'current':
      return <Chip size='small' color='primary' label={CERTIFICATION_STATUS_LABEL[status]} />
    case 'expiring_soon':
      return <Chip size='small' color='warning' variant='outlined' label={CERTIFICATION_STATUS_LABEL[status]} />
    case 'expired':
      return <Chip size='small' variant='outlined' sx={{ color: 'text.primary', borderColor: 'text.primary' }} label={CERTIFICATION_STATUS_LABEL[status]} />
    default:
      return <Chip size='small' variant='outlined' sx={{ color: 'text.secondary' }} label={CERTIFICATION_STATUS_LABEL[status]} />
  }
}
